// Global Instructions Rule Applied!
//
// Shape of POST /deals/pipeline-budget (view__deal_pipeline_budget): the
// Deal Pipeline → Budget tab. The cash lines as cfg.deal_pipeline_budget_accounts
// configures them (each with its total across the funds); one entry per
// followed fund with its capital (committed / called / uncalled), each cash
// line's debit-positive balance, net cash (their sum), open deal commitments
// and the two Remaining figures; one row per (deal, fund) open commitment;
// the totals across the funds.
import { z } from 'zod'
import { validateWithZodSchema } from '../../core/utils/utilities.ts'

const figures = {
  prog__committed: z.number().nullish(),
  prog__called: z.number().nullish(),
  prog__uncalled: z.number().nullish(),
  prog__net_cash: z.number().nullish(),
  prog__commitments: z.number().nullish(),
  prog__cash_remaining: z.number().nullish(),
  prog__capital_remaining: z.number().nullish()
}

// one configured cash line, in display order
export const DealPipelineBudgetAccountSchema = z
  .object({
    deal_pipeline_budget_accounts__id: z.string(),
    acc_gl_accounts__account_code: z.string().nullish(),
    acc_gl_accounts__account_name: z.string().nullish(),
    deal_pipeline_budget_accounts__label: z.string().nullish(),
    prog__total: z.number().nullish()
  })
  .passthrough()

// one cash line's balance on one fund: debit-positive, so a payable the fund
// owes is negative and one in debit (the fund is owed money) positive
export const DealPipelineBudgetFundAccountSchema = z
  .object({
    deal_pipeline_budget_accounts__id: z.string(),
    prog__balance: z.number().nullish()
  })
  .passthrough()

export const DealPipelineBudgetFundSchema = z
  .object({
    funds__id: z.string(),
    funds__name: z.string().nullish(),
    currencies__symbol: z.string().nullish(),
    // called including the lines the fund's terms leave out of the commitment
    prog__called_total: z.number().nullish(),
    prog__pct_drawn: z.number().nullish(),
    accounts: z.array(DealPipelineBudgetFundAccountSchema),
    ...figures
  })
  .passthrough()

export const DealPipelineBudgetTotalsSchema = z.object(figures).passthrough()

export const DealPipelineBudgetCommitmentSchema = z
  .object({
    deals__id: z.string(),
    deals__name: z.string().nullish(),
    funds__id: z.string(),
    pipeline_stages__name: z.string().nullish(),
    deals__pipeline_stage_date: z.string().nullish(),
    deal_commitments__amount: z.number().nullish()
  })
  .passthrough()

export const DealPipelineBudgetSchema = z
  .object({
    as_of: z.string().nullish(),
    accounts: z.array(DealPipelineBudgetAccountSchema),
    funds: z.array(DealPipelineBudgetFundSchema),
    commitments: z.array(DealPipelineBudgetCommitmentSchema),
    totals: DealPipelineBudgetTotalsSchema
  })
  .passthrough()

export type DealPipelineBudgetAccount = z.infer<typeof DealPipelineBudgetAccountSchema>
export type DealPipelineBudgetFund = z.infer<typeof DealPipelineBudgetFundSchema>
export type DealPipelineBudgetTotals = z.infer<typeof DealPipelineBudgetTotalsSchema>
export type DealPipelineBudgetCommitment = z.infer<typeof DealPipelineBudgetCommitmentSchema>
export type DealPipelineBudget = z.infer<typeof DealPipelineBudgetSchema>

/** The figure keys a fund entry and the totals share, in statement order. */
export type DealPipelineBudgetFigureKey = keyof typeof figures

export const validateDealPipelineBudget = (data: unknown): DealPipelineBudget =>
  validateWithZodSchema(DealPipelineBudgetSchema, data, 'deal pipeline budget')
